import { useQuery } from "@pinia/colada";

import { toSearchParams } from "@solvari/utils";

import { ziggy } from "@/shared/libs/ziggy";
import { kyCacheTags } from "~/shared/composables/useCacheTags.ts";
import { useQueryNormalize } from "~/shared/composables/useQueryNormalize.ts";
import { handleNuxtQuery } from "~/shared/libs/networkErrorHandling.ts";

function useProvince(params: { localeCode: string; slug: string }) {
  return {
    key: () => ["api.v3.geo.province", params],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.geo.province"), {
          searchParams: toSearchParams(params),
        })
        .json<App.DTO.Geo.ProvinceDTO>();
    }),
    staleTime: Infinity,
  };
}

function useProvinces(params: App.DTO.Geo.ProvincesRequestDTO) {
  const query = useQuery({
    key: () => ["api.v3.geo.provinces", params],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.geo.provinces"), {
          searchParams: toSearchParams(params),
        })
        .json<App.DTO.Geo.ProvinceDTO[]>();
    }),
    staleTime: Infinity,
  });

  useQueryNormalize(query.data, (data: App.DTO.Geo.ProvinceDTO) =>
    useProvince({ localeCode: data.locale, slug: data.slug }).key(),
  );

  return query;
}

function useMunicipality(params: { localeCode: string; slug: string }) {
  return {
    key: () => ["api.v3.geo.municipality", params],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.geo.municipality"), {
          searchParams: toSearchParams(params),
        })
        .json<App.DTO.Geo.MunicipalityDTO>();
    }),
    staleTime: Infinity,
  };
}

function useMunicipalities(
  params: App.DTO.Geo.MunicipalitiesRequestDTO,
  options?: { enabled?: () => boolean },
) {
  const query = useQuery({
    key: () => ["api.v3.geo.municipalities", params],
    query: handleNuxtQuery(() => {
      return kyCacheTags
        .get(ziggy("api.v3.geo.municipalities"), {
          searchParams: toSearchParams(params),
        })
        .json<App.DTO.Geo.MunicipalityDTO[]>();
    }),
    staleTime: Infinity,
    enabled: options?.enabled ?? true,
  });

  useQueryNormalize(query.data, (data: App.DTO.Geo.MunicipalityDTO) =>
    useMunicipality({ localeCode: data.locale, slug: data.slug }).key(),
  );

  return query;
}

export { useMunicipalities, useMunicipality, useProvince, useProvinces };
